'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import { useState, useRef, useEffect, useId } from 'react';

import type { HeaderNavigationCategory } from '../../utils/header-navigation-data';

import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { HiOutlineDotsHorizontal } from 'react-icons/hi';

import { MdOutlineKeyboardArrowRight, MdOutlineKeyboardArrowLeft } from 'react-icons/md';

const CATEGORY_DROPDOWN_OPEN_DELAY_MS = 180;

export function HeaderCategories({
  categories,
  className,
}: {
  categories: HeaderNavigationCategory[];
  className?: { container?: string };
}) {
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const hoverDelayRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const menuId = useId();

  const updateScrollArrows = () => {
    if (containerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  const handleScroll = (direction: 'left' | 'right') => {
    if (containerRef.current) {
      const scrollAmount = containerRef.current.clientWidth * 0.8;
      const newScrollPosition =
        direction === 'right'
          ? containerRef.current.scrollLeft + scrollAmount
          : containerRef.current.scrollLeft - scrollAmount;

      containerRef.current.scrollTo({
        left: newScrollPosition,
        behavior: 'smooth',
      });
    }
  };

  const handleMouseEnter = (slug: string) => {
    if (hoverDelayRef.current) {
      clearTimeout(hoverDelayRef.current);
      hoverDelayRef.current = null;
    }

    if (hoveredCategory !== null) {
      setHoveredCategory(slug);
      return;
    }

    hoverDelayRef.current = setTimeout(() => {
      setHoveredCategory(slug);
      hoverDelayRef.current = null;
    }, CATEGORY_DROPDOWN_OPEN_DELAY_MS);
  };

  const handleMouseLeave = () => {
    if (hoverDelayRef.current) {
      clearTimeout(hoverDelayRef.current);
      hoverDelayRef.current = null;
    }
    setHoveredCategory(null);
  };

  const handleLinkClick = () => {
    setHoveredCategory(null);
    if (hoverDelayRef.current) {
      clearTimeout(hoverDelayRef.current);
      hoverDelayRef.current = null;
    }
  };

  useEffect(() => {
    updateScrollArrows();
    const container = containerRef.current;

    if (container) {
      container.addEventListener('scroll', updateScrollArrows);
      window.addEventListener('resize', updateScrollArrows);
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', updateScrollArrows);
        window.removeEventListener('resize', updateScrollArrows);
      }
      if (hoverDelayRef.current) {
        clearTimeout(hoverDelayRef.current);
      }
    };
  }, []);

  return (
    <div className="custom-container relative w-full">
      <button
        onClick={() => handleScroll('left')}
        className={cn(
          'absolute left-2 top-1/2 z-20 -translate-y-1/2 transform rounded-full border border-border bg-white p-2 xl:-left-12',
          'transition-all duration-200',
          showLeftArrow ? 'opacity-100' : 'pointer-events-none opacity-0',
          'hover:bg-neutral-100',
        )}
        aria-label="Scroll left"
      >
        <MdOutlineKeyboardArrowLeft size={24} className="text-foreground" />
      </button>
      <div
        ref={containerRef}
        className={cn(
          'custom-container',
          'flex w-full items-center justify-start gap-0 overflow-x-auto',
          'hide-scroll-bar snap-x snap-mandatory scroll-smooth',
          className?.container,
        )}
      >
        {categories.map(category => {
          const hasChildren = category.childs && category.childs.length > 0;
          const isHovered = hoveredCategory === category.slug;

          return (
            <div
              key={category.slug}
              onMouseEnter={() => handleMouseEnter(category.slug)}
              onMouseLeave={handleMouseLeave}
              onFocusCapture={() => {
                if (hoverDelayRef.current) {
                  clearTimeout(hoverDelayRef.current);
                  hoverDelayRef.current = null;
                }
                setHoveredCategory(category.slug);
              }}
              onBlurCapture={event => {
                if (!event.currentTarget.contains(event.relatedTarget)) handleMouseLeave();
              }}
              onKeyDown={event => {
                if (event.key === 'Escape') {
                  event.preventDefault();
                  // Return focus before closing, so focus does not get lost in an unmounted panel.
                  event.currentTarget.querySelector<HTMLAnchorElement>('a')?.focus();
                  handleMouseLeave();
                }
              }}
              className="snap-start"
            >
              <Link
                prefetchOnHover
                prefetch={false}
                href={ROUTES.CATEGORY({ slug: category.slug, page: 1 })}
                onClick={handleLinkClick}
                aria-expanded={hasChildren ? isHovered : undefined}
                aria-controls={hasChildren && isHovered ? `${menuId}-${category.slug}` : undefined}
                className={cn(
                  'group transition-all',
                  'flex items-center justify-start gap-2 whitespace-nowrap bg-transparent px-3 py-4 text-sm text-foreground',
                  'hover:bg-neutral-50',
                )}
              >
                {category.icon?.url && (
                  <Image
                    src={category.icon?.url ?? '/images/placeholder.png'}
                    alt={resolveImageAlt(category?.icon, category?.name)}
                    width={0}
                    height={0}
                    className="min-h-[18px] min-w-[18px] object-contain object-center"
                  />
                )}
                {category.name}
              </Link>
              {hasChildren && isHovered && (
                <>
                  <div
                    id={`${menuId}-${category.slug}`}
                    className={cn(
                      'pointer-events-none fixed left-0 top-[230px] z-40 h-full w-full bg-black/30',
                      isHovered ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <div
                    className={cn(
                      'custom-section',
                      'absolute left-0 top-full z-50 w-full border-t border-t-primary bg-white py-8',
                      isHovered
                        ? 'pointer-events-auto opacity-100'
                        : 'pointer-events-none opacity-0',
                    )}
                  >
                    <div className={cn('custom-container', 'grid grid-cols-3 gap-x-8 gap-y-4')}>
                      {category.childs?.map(child => (
                        <Link
                          prefetchOnHover
                          prefetch={false}
                          href={ROUTES.CATEGORY({ slug: child.slug, page: 1 })}
                          key={child.slug}
                          onClick={handleLinkClick}
                          className="col-span-1 flex h-10 w-full items-center justify-between transition-all hover:opacity-80"
                        >
                          <div className="flex items-center justify-start gap-2">
                            {child?.image && child.image?.url && (
                              <Image
                                src={child.image.url}
                                alt={resolveImageAlt(child?.image, child?.name)}
                                width={40}
                                height={40}
                                className="h-10 w-10 shrink-0 object-contain object-center"
                                sizes="40px"
                              />
                            )}
                            <p className="text-sm font-normal text-foreground">{child.name}</p>
                          </div>
                          <MdOutlineKeyboardArrowRight size={16} className="text-primary" />
                        </Link>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          );
        })}
        <Link
          prefetchOnHover
          prefetch={false}
          href={ROUTES.CATEGORIES()}
          className={cn(
            'group snap-start transition-all',
            'flex items-center justify-start gap-2 bg-transparent px-3 py-4 text-sm text-foreground',
            'hover:bg-neutral-50',
          )}
        >
          <HiOutlineDotsHorizontal size={20} className="text-foreground" />
          Viac
        </Link>
      </div>
      <button
        onClick={() => handleScroll('right')}
        className={cn(
          'absolute right-2 top-1/2 z-20 -translate-y-1/2 transform rounded-full border border-border bg-white p-1.5 xl:-right-12',
          'transition-all duration-200',
          showRightArrow ? 'opacity-100' : 'pointer-events-none opacity-0',
          'hover:bg-neutral-100',
        )}
        aria-label="Scroll right"
      >
        <MdOutlineKeyboardArrowRight size={24} className="text-foreground" />
      </button>
    </div>
  );
}
